import type { Dayjs, ConfigType } from 'dayjs';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ADMIN_TIME_ZONE = 'America/New_York';
export const MATTO_TIME_ZONE = ADMIN_TIME_ZONE;

const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const CALENDAR_DATE_PREFIX_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

export function isCalendarDateString(value: ConfigType): value is string {
  return typeof value === 'string' && CALENDAR_DATE_PATTERN.test(value);
}

export function getCalendarDate(value: ConfigType): Dayjs {
  const calendarDateMatch =
    typeof value === 'string' ? value.match(CALENDAR_DATE_PREFIX_PATTERN) : null;

  return dayjs(calendarDateMatch?.[1] ?? value);
}

export function getDayBoundaryInTimeZone(
  value: Dayjs,
  timeZone: string,
  endOfDay: boolean
): number {
  const calendarDate = value.format(CALENDAR_DATE_FORMAT);
  const zonedDate = dayjs.tz(calendarDate, timeZone);

  return (endOfDay ? zonedDate.endOf('day') : zonedDate.startOf('day')).valueOf();
}

export function getDateInTimeZone(value: ConfigType, timeZone: string): Dayjs {
  return dayjs(value).tz(timeZone);
}

/**
 * Converts absolute timestamps to the Admin business timezone while leaving
 * calendar-only values unchanged. A value such as `2026-08-24` is a date, not
 * midnight in the administrator's browser timezone.
 */
export function getDateInAdminTimeZone(value: ConfigType): Dayjs {
  return isCalendarDateString(value) ? dayjs(value) : getDateInTimeZone(value, ADMIN_TIME_ZONE);
}

export function getAdminDayBoundary(value: Dayjs, endOfDay: boolean): number {
  return getDayBoundaryInTimeZone(value, ADMIN_TIME_ZONE, endOfDay);
}

export function getTimeOnRelativeAdminDay(
  baseValue: ConfigType,
  offsetDays: number,
  timeOfDay: string
): number {
  const calendarDate = getDateInTimeZone(baseValue, ADMIN_TIME_ZONE).format(CALENDAR_DATE_FORMAT);
  const targetCalendarDate = dayjs(calendarDate)
    .add(offsetDays, 'day')
    .format(CALENDAR_DATE_FORMAT);

  return dayjs
    .tz(`${targetCalendarDate} ${timeOfDay}:00`, ADMIN_TIME_ZONE)
    .millisecond(0)
    .valueOf();
}
